/**
 * Animation Config
 *
 * Tweak these values to adjust camera, wallet, and cash feel.
 * Save the file — Vite HMR reloads instantly.
 */

// ─────────────────────────────────────────────────────────────
// CAMERA
// ─────────────────────────────────────────────────────────────
export const CameraAnims = {
  // ===== TRANSITIONS (GSAP tweens) =====

  /** Default → Betting camera */
  toBettingDuration: 0.8,
  toBettingEase: 'power2.out',

  /** Betting → Default camera */
  toDefaultDuration: 0.8,
  toDefaultEase: 'power2.out',

  /** Return to default (no dice in view) */
  returnToDefaultDuration: 0.6,
  returnToDefaultEase: 'power2.out',

  /** Betting bounds zoom-out tween (after zones update) */
  bettingBoundsTweenDuration: 0.5,
  bettingBoundsTweenEase: 'power2.out',

  // ===== FOLLOW MODE (per-frame lerp) =====

  /** Base lerp speed for following rolling dice */
  followLerpSpeed: 6,

  /** Lerp speed when zoomed in on dice (collecting) */
  zoomLerpSpeed: 7,

  /** Speed multiplier when player is holding dice */
  heldDiceSpeedMultiplier: 2.5,

  // ===== BOUNDS & FRAMING =====

  /** Zoom out only when extent exceeds this (world units) */
  boundsZoomOutThreshold: 4,

  /** How aggressively to zoom out past the threshold (0–1) */
  boundsZoomOutScaling: 0.5,

  /** Tighter padding when zoomed in (0–1, smaller = tighter) */
  zoomPaddingScale: 0.6,

  /** FOV multiplier for depth calc in follow mode */
  followDepthFovMultiplier: 0.7,

  /** Minimum camera distance in non-zoom follow */
  minFollowDistance: 10,

  /** Minimum camera distance in zoom mode (prevents over-zoom on 1 die) */
  minZoomDistance: 10,

  // ===== SIDE BETTING =====

  /** Camera nudge toward create zone (0–1, 0.05 = subtle) */
  sideBetFocusStrength: 0.05,
};

// ─────────────────────────────────────────────────────────────
// HI_LO RESOLUTION
// ─────────────────────────────────────────────────────────────
export const HiLoAnims = {
  /** Delay before dice gear thumbnails animate out (ms) */
  diceGearOutDelay: 300,
  /** Duration for dice gear scale-out animation (seconds) */
  diceGearOutDuration: 0.3,
  /** Duration for bank icon + dice gear scale-in on winner (seconds) */
  winnerRevealDuration: 0.4,
  /** Pause after winner reveal before camera moves to betting (ms) */
  postRevealPause: 500,
};

// ─────────────────────────────────────────────────────────────
// DICE EQUIPPED FLYOUT (shooter announcement)
// ─────────────────────────────────────────────────────────────
export const DiceEquippedAnims = {
  /** Initial pop size (px) */
  popSize: 56,           // default: 56
  /** Gap between dice icons at spawn (px) */
  popGap: 3,             // default: 3
  /** Duration for icon pop-in at screen center (seconds) */
  popInDuration: 0.38,   // default: 0.28
  popInEase: 'back.out(1.7)',
  /** Base delay before first icon pops in (seconds) */
  popInBaseDelay: 0.08,  // default: 0.08
  /** Stagger between icons popping in (seconds) */
  popInStagger: 0.105,   // default: 0.06
  /** Timeline position when flight begins (seconds) */
  flyStart: 0.55,        // default: 0.55
  /** Duration of straight-line flight to profile (seconds) */
  flyDuration: 0.86,     // default: 0.5
  flyEase: 'power2.inOut',
};

// ─────────────────────────────────────────────────────────────
// DICE RESULT FLYOUT
// ─────────────────────────────────────────────────────────────
export const DiceResultAnims = {
  /** Duration for icon scale-in at dice position (seconds) */
  scaleInDuration: 0.3,
  /** Peak scale before flight */
  scaleInPeak: 1.5,
  scaleInEase: 'back.out(1.7)',
  /** Stagger between icons scaling in (seconds) */
  scaleInStagger: 0.08,
  /** Pause after scale-in before flight begins (seconds) */
  preFlightPause: 0.1,
  /** Duration of arc flight to profile container (seconds) */
  flyDuration: 0.4,
  flyEase: 'power2.inOut',
  /** Stagger between icons taking flight (seconds) */
  flyStagger: 0.06,
  /** Pixels above the midpoint for the arc control point */
  flyArcHeight: 60,
  /** Flyer opacity fade-out starts at this progress (0-1, 0.85 = last 15%) */
  flyerFadeStart: 0.85,
  /** Number of trailing ghost copies per flyer */
  trailCount: 2,
  /** Delay between each trail ghost (seconds) */
  trailDelay: 0.04,
  /** Lander pop-in duration in the profile container (seconds) */
  landerDuration: 0.3,
  landerEase: 'back.out(1.7)',
  /** Stagger for lander pop-in (seconds) */
  landerStagger: 0.06,
  /** Pixels above the dice spawn point where the result label sits. */
  labelOffsetY: 56,
  /** Label scale-in duration (s). */
  labelScaleInDuration: 0.28,
  labelScaleInEase: 'back.out(2.0)',
  /** Hold time (s) AFTER the dice landers complete before the label fades out. */
  labelHoldAfterLand: 0.18,
  /** Label fade-out duration (s). */
  labelFadeOutDuration: 0.32,
  labelFadeOutEase: 'power2.in',
};

// ─────────────────────────────────────────────────────────────
// CASH WIN FLYOUT
// ─────────────────────────────────────────────────────────────
export const CashWinAnims = {
  /** Duration for note scale-in at spawn position (seconds) */
  scaleInDuration: 0.25,
  /** Peak scale before flight */
  scaleInPeak: 1.4,
  scaleInEase: 'back.out(1.7)',
  /** Stagger between notes spawning (seconds) */
  noteStagger: 0.12,
  /** Pause after scale-in before flight begins (seconds) */
  preFlightPause: 0.15,
  /** Duration of arc flight to profile (seconds) */
  flyDuration: 0.5,
  flyEase: 'power2.inOut',
  /** Pixels above the midpoint for the arc control point */
  flyArcHeight: 80,
  /** Flyer opacity fade-out starts at this progress (0-1) */
  flyerFadeStart: 0.8,
  /** Number of trailing ghost copies per note */
  trailCount: 2,
  /** Delay between each trail ghost (seconds) */
  trailDelay: 0.05,
  /** Size of the small note image (px) */
  noteSize: 36,
  /** Max notes per flyout burst (caps visual clutter) */
  maxNotes: 5,
  /** Amount text pop-in duration at profile after notes land (seconds) */
  amountPopDuration: 0.35,
  amountPopEase: 'back.out(1.7)',
  /** How long the amount text stays visible before fading (seconds) */
  amountLingerDuration: 1.0,
  /** Amount text fade-out duration (seconds) */
  amountFadeDuration: 0.3,

  // ── Bet icon indicator (shown before money flyout) ──
  /** Size of each bet icon (px) */
  betIconSize: 48,
  /** Horizontal gap between bet icons (px) */
  betIconGap: 6,
  /** Vertical offset above the spawn point (px) */
  betIconYOffset: 70,
  /** Duration of icon zoom-in (seconds) */
  betIconInDuration: 0.35,
  betIconInEase: 'back.out(1.7)',
  /** Stagger between icons zooming in (seconds) */
  betIconInStagger: 0.08,
  /** Fade-out duration when money flyout starts (seconds) */
  betIconOutDuration: 0.25,
  /** Stagger for fade-out (seconds) */
  betIconOutStagger: 0.05,
};

// ─────────────────────────────────────────────────────────────
// PROFILE BALANCE WIN-BUMP
// Plays on a winning player's profile-card balance AFTER the cash-win
// flyout lands — so the number moves the instant the cash arrives
// instead of popping in a full game:update later. Scale up → count up
// (fixed duration, amount-independent) → scale back down with ease-back.
// ─────────────────────────────────────────────────────────────
export const BalanceBumpAnims = {
  /** Peak scale at the top of the bump */
  peakScale: 1.1,
  /** Scale-up duration (seconds) */
  scaleUpDuration: 0.16,
  scaleUpEase: 'back.out(2)',
  /** Count-up duration — SAME for every win amount (seconds) */
  countDuration: 0.55,
  countEase: 'power1.out',
  /** Scale-down duration (seconds) */
  scaleDownDuration: 0.28,
  scaleDownEase: 'back.in(2)',
};

// ─────────────────────────────────────────────────────────────
// PROFILE SIDEBET INDICATORS
// ─────────────────────────────────────────────────────────────
export const ProfileSidebetAnims = {
  /** Icon size (px) inside profile-card__bets */
  iconSize: 24,
  /** Gap between icons (px) */
  iconGap: 3,
  /** Scale-in duration when a sidebet is added (seconds) */
  scaleInDuration: 0.35,
  scaleInEase: 'back.out(1.7)',
  /** Count bubble pulse when count increments */
  countPulseDuration: 0.25,
  countPulseScale: 1.3,
  countPulseEase: 'back.out(2)',
};

// ─────────────────────────────────────────────────────────────
// LIGHTING
// ─────────────────────────────────────────────────────────────
export const LightAnims = {
  /** Crossfade duration when switching between shooting ↔ betting anchors */
  transitionDuration: 0.8,
  // Match the camera's betting transition ease (CameraAnims.toBetting/toDefault
  // are both 'power2.out'). With 'power2.inOut' the lights eased in slowly and
  // trailed the camera's decelerating move — they read as finishing AFTER the
  // camera. Same curve → lights and camera track and settle together.
  transitionEase: 'power2.out',
};

// ─────────────────────────────────────────────────────────────
// WALLET
// ─────────────────────────────────────────────────────────────
export const WalletAnims = {
  // ===== OPEN / CLOSE =====
  // "Anticipate" feel (chosen 2026-06-09 via the wallet-anim debug panel):
  // open pulls back slightly then springs forward; close accelerates away with
  // a touch of wind-up. Replaces the old flat sine.out, which read mechanical.

  /** Wallet open animation duration (seconds) */
  openDuration: 0.72,
  openEase: 'back.inOut(1.8)',

  /** Wallet close animation duration (seconds) */
  closeDuration: 0.5,
  closeEase: 'back.in(2)',

  // ===== ENTRY (slide-in from off-screen) =====

  /** Entry animation duration (seconds) */
  entryDuration: 0.6,
  entryEase: 'sine.out',

  /** Starting openProgress — negative = below viewport */
  entryStartProgress: -0.3,

  // ===== BILL SELECTION FAN =====

  /** Fan-out/collapse duration when switching denominations (seconds) */
  selectionFanDuration: 0.25,
  selectionFanEase: 'sine.out',

  /** Vertical spacing per bill in fan mode */
  selectionFanSpacing: 0.1,

  /** Delay before popping selected bill after denomination change (ms) */
  selectedBillDelay: 60,

  /** Delay before rebuilding bills after selection (ms) */
  rebuildDelay: 300,

  // ===== BILL HIGHLIGHT =====

  /** Emissive intensity for selected bill */
  highlightEmissive: 0.7,

  /** Emissive intensity for unselected bills */
  dimEmissive: 0.3,

  /** Scale multiplier for selected bill */
  highlightScale: 1.1,

  // ===== BILL RETURN =====

  /** Delay before disposing returned bill (ms) */
  returnDisposeDelay: 500,
};

// ─────────────────────────────────────────────────────────────
// CASH / BILLS
// ─────────────────────────────────────────────────────────────
export const CashAnims = {
  // ===== BILL STACK (accumulated bills in hand) =====

  /** Duration for stack return to rest position (ms) */
  stackReturnDuration: 250,

  /** Duration for bill lift animation when extracting from wallet (ms) */
  billLiftDuration: 300,

  /** Min random bend during bill lift (negative = curl down) */
  billLiftBendMin: -0.05,

  /** Max additional random bend range */
  billLiftBendRange: -0.35,

  /** Z-spacing between stacked bills */
  stackBillSpacing: 0.05,

  /** Height of drag plane for raycasting */
  dragPlaneHeight: 1.8,

  /** Y rise when dragging stack */
  dragRiseY: 0.2,

  // ===== ZONE DROP (bills falling into bet zone) =====

  /** Duration for drop phase — bills fall to zone center (ms) */
  zoneDropDuration: 300,

  /** Duration for scatter phase — bills spread outward after impact (ms) */
  zoneScatterDuration: 200,

  /** Peak bend amount during drop fall */
  zoneDropBendPeak: 1.0,

  /** Max bounce height on impact */
  zoneBounceMax: 0.2,

  /** Min bounce height on impact */
  zoneBounceMin: 0.05,

  /** Push distance range min (scatter outward from center) */
  zoneScatterPushMin: 0.2,

  /** Push distance range size (added to min) */
  zoneScatterPushRange: 0.3,

  // ===== BILL PHYSICS FEEL =====

  /** Bill bend lerp speed (higher = snappier bends) */
  bendLerpSpeed: 8,

  /** Bill position lerp speed (non-physics movement) */
  positionLerpSpeed: 10,

  /** Bend strength multiplier (how much bills curl) */
  bendStrengthMultiplier: 0.5,

  // ===== LOCK-IN BUTTON =====

  /** Slide-in duration (seconds) */
  lockInEnterDuration: 0.5,
  lockInEnterEase: 'back.out(1)',

  /** Initial tilt angle (radians, like a coin on edge) */
  lockInEnterTilt: 1.4,

  /** Rotation settle duration (seconds) */
  lockInRotationDuration: 0.5,
  lockInRotationEase: 'power3.out',

  /** Slide-out duration (seconds) */
  lockInExitDuration: 0.4,
  lockInExitEase: 'power2.in',

  /** Off-screen X offset (world units, negative = left) */
  lockInOffscreenOffset: -3.5,
};
